import { PresenceDataModeType } from '@lib/api/entities';
import { FsDataDogRumService } from '@lib/data-dog-rum';

export const PRESENCE_LOG_THROTTLE_MS = 240000; // 4 minutes

const lastLogTimes: Map<ThrottleKey, number> = new Map();

export type ThrottleKey =
  | 'map-service-status-fetching'
  | 'map-api-call'
  | 'map-markers-created'
  | 'map-maps-devices-response'
  | 'map-device-details-response'
  | 'drawer-service-status-fetching'
  | 'drawer-markers-created'
  | 'drawer-device-list-response';

export type PresencePollingStoppedReason =
  | 'user_action'
  | 'service_destroyed'
  | 'layer_removed'
  | 'token_lost'
  | 'token_revoked'
  | 'became_inactive'
  | 'drawer_closed'
  | 'mode_changed'
  | 'error'
  | 'manual_stop'
  | 'service_destroyed_remove_from_map'
  | 'cleanup_before_restart_shadow_mode_polling'
  | 'service_destroyed_ngOnDestroy'
  | 'isActive_toggled';

export type PresenceServiceStatusReason =
  | 'no_token'
  | 'not_active'
  | 'no_agency_id'
  | 'token_received'
  | 'missing_scope'
  | 'will_start_fetch_layer_added_to_map'
  | 'will_start_fetch_devices';

function shouldThrottle(key: ThrottleKey, throttleMs: number = PRESENCE_LOG_THROTTLE_MS): boolean {
  const now = Date.now();
  const lastTime = lastLogTimes.get(key) || 0;

  if (now - lastTime >= throttleMs) {
    lastLogTimes.set(key, now);
    return false;
  }
  return true;
}

export function logPresencePollingStopped(
  datadogService: FsDataDogRumService,
  params: {
    service: 'map' | 'drawer';
    mode?: PresenceDataModeType;
    reason: PresencePollingStoppedReason;
    wasFetching: boolean;
    organizationId: string;
    agencyId?: string;
    userId: string;
  },
): void {
  datadogService.addAction('axon_presence_polling_stopped', {
    service: params.service,
    mode: params.mode ? convertModeToString(params.mode) : undefined,
    reason: params.reason,
    was_fetching: params.wasFetching,
    organization_id: params.organizationId,
    agency_id: params.agencyId,
    user_id: params.userId,
    timestamp: new Date().toISOString(),
  });
}

export function logPresenceServiceStatus(
  datadogService: FsDataDogRumService,
  params: {
    service: 'map' | 'drawer';
    isFetching: boolean;
    mode?: PresenceDataModeType;
    hasToken: boolean;
    isActive: boolean;
    hasAgencyId: boolean;
    agencyId?: string;
    userId: string;
    organizationId: string;
    reason?: PresenceServiceStatusReason;
  },
  throttleKey?: ThrottleKey,
): void {
  if (throttleKey && shouldThrottle(throttleKey)) {
    return;
  }

  datadogService.addAction('axon_presence_service_status', {
    service: params.service,
    is_fetching: params.isFetching,
    mode: params.mode ? convertModeToString(params.mode) : undefined,
    has_token: params.hasToken,
    is_active: params.isActive,
    has_agency_id: params.hasAgencyId,
    agency_id: params.agencyId,
    user_id: params.userId,
    reason: params.reason,
    organization_id: params.organizationId,
    timestamp: new Date().toISOString(),
  });
}

export function logPresenceApiCall(
  datadogService: FsDataDogRumService,
  params: {
    service: 'map' | 'drawer';
    endpoint: 'maps_devices' | 'device_list' | 'device_details';
    mode: PresenceDataModeType;
    organizationId: string;
    agencyId: string;
    userId: string;
  },
  throttleKey?: ThrottleKey,
): void {
  if (throttleKey && shouldThrottle(throttleKey)) {
    return;
  }

  datadogService.addAction('axon_presence_api_call', {
    service: params.service,
    endpoint: params.endpoint,
    mode: convertModeToString(params.mode),
    organization_id: params.organizationId,
    agency_id: params.agencyId,
    user_id: params.userId,
    timestamp: new Date().toISOString(),
  });
}

export function logPresenceMarkersCreated(
  datadogService: FsDataDogRumService,
  params: {
    service: 'map' | 'drawer';
    mode: PresenceDataModeType;
    markersCreated: number;
    markersOnMap: number;
    organizationId: string;
    agencyId: string;
    userId: string;
  },
  throttleKey?: ThrottleKey,
): void {
  if (throttleKey && shouldThrottle(throttleKey)) {
    return;
  }

  datadogService.addAction('axon_presence_markers_created', {
    service: params.service,
    mode: convertModeToString(params.mode),
    markers_created: params.markersCreated,
    markers_on_map: params.markersOnMap,
    organization_id: params.organizationId,
    agency_id: params.agencyId,
    user_id: params.userId,
    timestamp: new Date().toISOString(),
  });
}

export function logPresenceApiResponse(
  datadogService: FsDataDogRumService,
  params: {
    service: 'map' | 'drawer';
    endpoint: 'maps_devices' | 'device_list' | 'device_details';
    statusCode: number;
    totalFeatures: number;
    correlationId?: string;
    organizationId: string;
    agencyId: string;
    userId: string;
  },
  throttleKey?: ThrottleKey,
): void {
  if (throttleKey && shouldThrottle(throttleKey)) {
    return;
  }

  datadogService.addAction('axon_presence_api_response', {
    service: params.service,
    endpoint: params.endpoint,
    status_code: params.statusCode,
    total_features: params.totalFeatures,
    correlation_id: params.correlationId,
    organization_id: params.organizationId,
    agency_id: params.agencyId,
    user_id: params.userId,
    timestamp: new Date().toISOString(),
  });
}

function convertModeToString(mode: PresenceDataModeType): 'enabled' | 'shadow' | 'disabled' {
  switch (mode) {
    case PresenceDataModeType.PRESENCE_API:
      return 'enabled';
    case PresenceDataModeType.SHADOW:
      return 'shadow';
    case PresenceDataModeType.DEVICE_SEARCH_API:
    default:
      return 'disabled';
  }
}
